import React, { useCallback, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import NumberedForm from 'UI/components/molecules/NumberedForm';
import { Endpoints } from 'UI/constants/endpoints';
import {
  idOptionSelected,
  industrySpecialtyOptionLabel,
  REQUIRED_VALIDATION,
  titleOptionLabel
} from 'UI/utils';

import { FORM_FIELDS_MAP } from '../../fields';

const COPIES = strings.searchProjects.forms.searchProject;

export const IndustryDetailsForm = ({ isCompany = false }) => {
  const isSubspecialtyRequiredRef = useRef(false);
  const isPositionRequiredRef = useRef(false);
  const { control, errors, setValue, watch, clearError } = useFormContext();
  const specialtyValue = watch(FORM_FIELDS_MAP.specialty.key);

  const handleSelectChange = ({ dependentFields = null, value }) => {
    if (dependentFields) {
      const fields = Array.isArray(dependentFields) ? dependentFields : [dependentFields];
      fields.forEach(field => setValue(field, null));

      if (fields.includes(FORM_FIELDS_MAP.subspecialty.key)) {
        isSubspecialtyRequiredRef.current = false;
        clearError(FORM_FIELDS_MAP.subspecialty.key);
      }

      if (fields.includes(FORM_FIELDS_MAP.position.key)) {
        isPositionRequiredRef.current = false;
        clearError(FORM_FIELDS_MAP.position.key);
      }
    }

    return value;
  };

  const subspecialtyUrl = specialtyValue?.id
    ? Endpoints.Subspecialties.replace(':id', specialtyValue.id)
    : null;
  const positionUrl = specialtyValue?.id
    ? `${Endpoints.Positions}?specialtyId=${specialtyValue.id}`
    : null;

  const handleSubspecialtiesLoaded = useCallback(options => {
    isSubspecialtyRequiredRef.current = Array.isArray(options) && options.length > 0;
  }, []);

  const handlePositionsLoaded = useCallback(options => {
    isPositionRequiredRef.current = Array.isArray(options) && options.length > 0;
  }, []);

  return (
    <NumberedForm.Item title={COPIES.sections.titles.industryDetails}>
      <Grid container>
        <Grid item xs={12}>
          <Alert severity="info">{COPIES.sections.industryDetails.alertInfo}</Alert>
        </Grid>

        <Grid item xs={12}>
          <Controller
            as={
              <AutocompleteSelect
                error={!!errors[FORM_FIELDS_MAP.specialty.key]}
                errorText={errors[FORM_FIELDS_MAP.specialty.key]?.message}
                getOptionLabel={industrySpecialtyOptionLabel}
                getOptionSelected={idOptionSelected}
                groupBy={option => option.industry_title}
                name={FORM_FIELDS_MAP.specialty.key}
                placeholder={COPIES.sections.industryDetails.fields.industrySpecialty.placeholder}
                renderOption={titleOptionLabel}
                url={Endpoints.Specialties}
              />
            }
            control={control}
            defaultValue={null}
            name={FORM_FIELDS_MAP.specialty.key}
            onChange={([, value]) =>
              handleSelectChange({
                dependentFields: isCompany
                  ? [FORM_FIELDS_MAP.subspecialty.key]
                  : [FORM_FIELDS_MAP.subspecialty.key, FORM_FIELDS_MAP.position.key],
                value
              })
            }
            onChangeName="onSelect"
            rules={REQUIRED_VALIDATION}
            valueName="selectedValue"
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            as={
              <AutocompleteSelect
                error={
                  isSubspecialtyRequiredRef.current && !!errors[FORM_FIELDS_MAP.subspecialty.key]
                }
                errorText={
                  isSubspecialtyRequiredRef.current
                    ? errors[FORM_FIELDS_MAP.subspecialty.key]?.message
                    : undefined
                }
                name={FORM_FIELDS_MAP.subspecialty.key}
                onOptionsLoaded={handleSubspecialtiesLoaded}
                placeholder={COPIES.sections.industryDetails.fields.subspecialty.placeholder}
                renderOption={titleOptionLabel}
                url={subspecialtyUrl}
              />
            }
            control={control}
            defaultValue={null}
            name={FORM_FIELDS_MAP.subspecialty.key}
            onChange={([, value]) => handleSelectChange({ value })}
            onChangeName="onSelect"
            rules={{
              validate: value => {
                if (!isSubspecialtyRequiredRef.current) return true;
                return Boolean(value) || REQUIRED_VALIDATION.required;
              }
            }}
            valueName="selectedValue"
          />
        </Grid>

        <When condition={!isCompany}>
          <Grid item xs={12}>
            <Controller
              as={
                <AutocompleteSelect
                  error={isPositionRequiredRef.current && !!errors[FORM_FIELDS_MAP.position.key]}
                  errorText={
                    isPositionRequiredRef.current
                      ? errors[FORM_FIELDS_MAP.position.key]?.message
                      : undefined
                  }
                  name={FORM_FIELDS_MAP.position.key}
                  onOptionsLoaded={handlePositionsLoaded}
                  placeholder={COPIES.sections.industryDetails.fields.position.placeholder}
                  url={positionUrl}
                />
              }
              control={control}
              defaultValue={null}
              name={FORM_FIELDS_MAP.position.key}
              onChange={([, value]) => handleSelectChange({ value })}
              onChangeName="onSelect"
              rules={{
                validate: value => {
                  if (!isPositionRequiredRef.current) return true;
                  return Boolean(value) || REQUIRED_VALIDATION.required;
                }
              }}
              valueName="selectedValue"
            />
          </Grid>
        </When>
      </Grid>
    </NumberedForm.Item>
  );
};
